import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { GeneralLedgerService } from '../../services/general-ledger.service';
import { TransactionDetailModalComponent, GlTransactionDetailData } from '../account-inquiry/transaction-detail-modal/transaction-detail-modal.component';

export interface AccountDetailModalData {
	accountcode: string;
	accountname: string;
	periodno: number | string;
	periodLabel: string;
}

/** Emitted when closing so callers can refresh lists (e.g. Simple Trial Balance tag breakdown). */
export interface AccountDetailModalCloseResult {
	dataChanged?: boolean;
}

interface GlTransaction {
	type: string;
	number: string;
	trandate: string;
	debit: string;
	credit: string;
	check: string;
	narrative: string;
	balance: string;
	tag: string;
	counterindex: number;
	tagref: number | null;
}

@Component({
	selector: 'app-account-detail-modal',
	templateUrl: './account-detail-modal.component.html',
	styleUrls: ['./account-detail-modal.component.scss']
})
export class AccountDetailModalComponent implements OnInit {
	/**
	 * Set when a transaction line save succeeds. Read from `MatDialogRef.beforeClosed` (backdrop/Esc
	 * do not go through `close()`, so the close result alone is not enough).
	 */
	parentShouldRefreshLists = false;

	loading = false;
	transactions: GlTransaction[] = [];
	endbalance: number | null = null;
	periodtotal: number | null = null;
	/** Period opening (chartdetails.bfwd) from API — P&L and B/S use this for running balance. */
	openingBalance: number | null = null;
	chartdetails: { bfwd: number; actual: number; balance: number } | null = null;

	constructor(
		public dialogRef: MatDialogRef<AccountDetailModalComponent, AccountDetailModalCloseResult>,
		@Inject(MAT_DIALOG_DATA) public data: AccountDetailModalData,
		private generalLedgerService: GeneralLedgerService,
		private dialog: MatDialog
	) {}

	ngOnInit(): void {
		this.load();
	}

	load(): void {
		this.loading = true;
		const payload = {
			glaccount: this.data.accountcode,
			from_period: this.data.periodno,
			to_period: this.data.periodno,
			gl_tag: '',
			posted: '0'
		};
		this.generalLedgerService.getAccountInquiry(payload).subscribe({
			next: (results: any) => {
				const period = results?.periods?.[0];
				this.transactions = period?.transactions || [];
				this.endbalance = parseFloat(results?.endbalance) || 0;
				this.periodtotal = parseFloat(period?.periodtotal) || 0;
				const sb = period?.startbalance;
				this.openingBalance = sb !== undefined && sb !== null && sb !== '' ? parseFloat(String(sb).replace(/,/g, '')) : null;
				if (this.openingBalance !== null && Number.isNaN(this.openingBalance)) {
					this.openingBalance = null;
				}
				const cd = results?.chartdetails;
				this.chartdetails = cd ? {
					bfwd:    parseFloat(cd.bfwd)    || 0,
					actual:  parseFloat(cd.actual)  || 0,
					balance: parseFloat(cd.balance) || 0,
				} : null;
				this.loading = false;
			},
			error: () => {
				this.transactions = [];
				this.loading = false;
			}
		});
	}

	openDetail(trans: GlTransaction): void {
		const detail: GlTransactionDetailData = {
			type: trans.type,
			number: trans.number,
			trandate: trans.trandate,
			debit: parseFloat(trans.debit) || 0,
			credit: parseFloat(trans.credit) || 0,
			chequeno: trans.check,
			narrative: trans.narrative,
			balance: parseFloat(trans.balance) || 0,
			tag: trans.tag,
			periodno: this.data.periodno,
			periodname: this.data.periodLabel,
			counterindex: trans.counterindex,
			tagref: trans.tagref ?? null
		};
		this.dialog.open(TransactionDetailModalComponent, {
			width: '480px',
			data: detail
		}).afterClosed().subscribe((result: any) => {
			if (result?.saved) {
				this.parentShouldRefreshLists = true;
				this.load();
			}
		});
	}

	close(): void {
		this.dialogRef.close({ dataChanged: this.parentShouldRefreshLists });
	}

	showOpeningBalanceRow(): boolean {
		return this.openingBalance != null && Math.abs(this.openingBalance) > 0.0005;
	}
}
